import { PageWrapper } from '@components'
import { ACCOUNT_NAVIGATION } from '@constants'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PasswordChangeForm } from 'src/partials/forms/account/password-change.form'
import { ProfileForm } from 'src/partials/forms/account/profile.form'
import { useIsMobile } from 'src/utilities/hooks/useIsMobile'

export const AccountView = observer(() => {
  const { hash } = useLocation()
  const navigate = useNavigate()
  const isMobile = useIsMobile()

  const getActiveSection = useMemo(() => {
    if (!hash) return 'profiel'
    return hash.replace('#', '')
  }, [hash])

  const handleSectionChange = (newHash: string) => {
    navigate(`/account${newHash}`)
  }

  // Flatten navigation items for mobile tabs
  const flatNavigationItems = useMemo(() => {
    return ACCOUNT_NAVIGATION.flatMap(section => section.items)
  }, [])

  const renderContent = () => {
    switch (getActiveSection) {
      case 'profiel':
        return <ProfileForm />
      case 'wachtwoord':
        return <PasswordChangeForm />
      case 'voorkeuren':
        return (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              App-voorkeuren
            </Typography>
            <Typography color="text.secondary">
              Voorkeuren formulier komt hier (Phase 4)
            </Typography>
          </Paper>
        )
      default:
        return <ProfileForm />
    }
  }

  return (
    <PageWrapper pageTitle="Mijn account">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {isMobile ? (
          /* Mobile: Horizontal Tabs */
          <Paper sx={{ width: '100%' }}>
            <Tabs
              value={getActiveSection}
              onChange={(_, newValue) => {
                const item = flatNavigationItems.find(item => item.value === newValue)
                if (item) handleSectionChange(item.hash)
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
              }}>
              {flatNavigationItems.map((item) => (
                <Tab
                  key={item.value}
                  label={item.label}
                  value={item.value}
                />
              ))}
            </Tabs>
          </Paper>
        ) : (
          /* Desktop: Keep original layout with sidebar */
          <Box sx={{ display: 'flex', gap: 3 }}>
            {/* Sidebar */}
            <Paper
              sx={{
                width: 250,
                flexShrink: 0,
                p: 2,
              }}>
              <Stack direction="column" gap={3}>
                {ACCOUNT_NAVIGATION.map((section) => (
                  <Box key={section.section}>
                    <Typography
                      variant="caption"
                      sx={{
                        px: 2,
                        py: 1,
                        display: 'block',
                        color: 'text.secondary',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}>
                      {section.section}
                    </Typography>
                    <List disablePadding>
                      {section.items.map((item) => (
                        <ListItem key={item.value} disablePadding>
                          <ListItemButton
                            selected={getActiveSection === item.value}
                            onClick={() => handleSectionChange(item.hash)}
                            sx={{
                              borderRadius: 1,
                              '&.Mui-selected': {
                                backgroundColor: 'primary.main',
                                color: 'primary.contrastText',
                                '&:hover': {
                                  backgroundColor: 'primary.dark',
                                },
                                '& .MuiListItemIcon-root': {
                                  color: 'primary.contrastText',
                                },
                              },
                            }}>
                            <ListItemIcon>
                              <item.icon />
                            </ListItemIcon>
                            <ListItemText primary={item.label} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ))}
              </Stack>
            </Paper>

            {/* Content */}
            <Box sx={{ flex: 1 }}>{renderContent()}</Box>
          </Box>
        )}

        {/* Mobile: Full-width content */}
        {isMobile && (
          <Box sx={{ width: '100%' }}>{renderContent()}</Box>
        )}
      </Box>
    </PageWrapper>
  )
})
